'use client';
import Bugsnag from "@bugsnag/js";
import {useEffect} from 'react';
import {useTekstClient} from "hooks/useTekstClient";
import {useRouter} from "next/navigation";

export default function ErrorPage({error, reset, errorType = "default"}) {
    const {tekst} = useTekstClient()
    const router = useRouter()
    useEffect(() => {
        // React 19: Errors caught by boundaries are logged via console.error
        Bugsnag.notify(error, (event) => {
            // Add error digest if available (Next.js 13+ provides this for debugging)
            if (error.digest) {
                event.addMetadata('error-page', {
                    digest: error.digest,
                    message: error.message,
                    stack: error.stack,
                })
            } else {
                event.addMetadata('error-page', {
                    message: error.message,
                    stack: error.stack,
                })
            }
        })

        // eslint-disable-next-line no-console
        console.error('Error boundary caught:', error)
    }, [error]);

    let header, tekstMsg, button, comment, paragraph1, paragraph2Intro, paragraph2Highlight1, paragraph2Middle, paragraph2Highlight2;
    if (errorType === 'content') {
        header = "algemeen:errorpagina:content:header";
        comment = "algemeen:errorpagina:content:comment";
        paragraph1 = "algemeen:errorpagina:content:paragraph1";
        paragraph2Intro = "algemeen:errorpagina:content:paragraph2_intro";
        paragraph2Highlight1 = "algemeen:errorpagina:content:paragraph2_highlight1";
        paragraph2Middle = "algemeen:errorpagina:content:paragraph2_middle";
        paragraph2Highlight2 = "algemeen:errorpagina:content:paragraph2_highlight2";
        button = "algemeen:errorpagina:content:button";
    } else {
        header = "algemeen:errorpagina:header";
        tekstMsg = "algemeen:errorpagina:tekst";
        button = "algemeen:errorpagina:button";
    }

    // Renders a comment-style line, coloring everything after "-> " like a code literal.
    function renderComment(text) {
        const idx = text.indexOf('-> ');
        if (idx === -1) return <span>{text}</span>;
        const prefix = text.slice(0, idx + 3);
        const rest = text.slice(idx + 3);
        return (
            <>
                <span>{prefix}</span>
                <span className="text-[#4da3ff]">{rest}</span>
            </>
        );
    }

    if (errorType === 'content') {
        return (
            <main className="flex h-full min-h-full flex-col items-center justify-center bg-white px-8 py-10 font-mono sm:px-16 sm:py-16">
                <style>{`
                    .error-try-again-btn {
                        background-color: #fecb47;
                        transition: background-color 0.2s ease;
                    }
                    .error-try-again-btn:hover {
                        background-color: #febd14;
                    }
                `}</style>
                <div className="flex max-w-2xl flex-col items-start text-left">
                    <h2 className="text-2xl font-bold text-[#4da3ff] sm:text-3xl">
                        {'{'}{tekst(header)}{'}'}
                    </h2>
                    <p className="mt-4 text-sm text-gray-500">
                        <span>// </span>
                        {renderComment(tekst(comment))}
                    </p>
                    <p className="mt-6 text-base leading-relaxed text-gray-700">
                        {tekst(paragraph1)}
                    </p>
                    <p className="mt-4 text-base leading-relaxed text-gray-700">
                        {tekst(paragraph2Intro)}
                        <br/>
                        <span>{tekst(paragraph2Highlight1)}</span>
                        <span>{tekst(paragraph2Middle)}</span>
                        <span>{tekst(paragraph2Highlight2)}</span>
                    </p>
                    <button
                        className="error-try-again-btn mt-6 rounded-md px-5 py-2 text-sm font-medium text-gray-800"
                        onClick={() => { router.push("/mijn-mkg/support/kenniscentrum") }}
                    >
                        {tekst(button)}
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main className="flex h-full flex-col items-center justify-center py-5">
            <style>{`
                .error-try-again-btn {
                    background-color: #fecb47;
                    transition: background-color 0.2s ease;
                }
                .error-try-again-btn:hover {
                    background-color: #febd14;
                }
            `}</style>
            <h2 className="text-center">{tekst(header)}</h2>
            <p>{tekst(tekstMsg)}</p>
            <button
                className="mt-4 rounded-md bg-blue-500 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-400"
                onClick={() => reset()}>
                {tekst(button)}
            </button>
        </main>
    );
}